import { css, styled } from 'styled-components'

import { Button, InteractiveIcon } from '@b-stock/fe-core/bstock-react'
import { Colors, pxToRem, Typography } from '@b-stock/fe-core/design-system'

import { Card } from '@/buyer-portal/components/layouts/CardLayout/card/ui'

export const ActionCard = styled(Card)`
  border-color: ${Colors.Semantic.BStock.default.lightBackground};
`

/**
 * Header chrome — shared by BidHeader and SimpleOfferHeader.
 */

export const Header = styled.div<{ $hasBanner?: boolean }>`
  background: ${Colors.Semantic.BStock.default.lightBackground};
  border-radius: ${(o) => (o.$hasBanner ? '0' : '0.5rem 0.5rem 0 0')};
  color: white;
  display: flex;
  gap: 1rem;
  justify-content: space-between;
  padding: 1rem 1rem 0.75rem;
`

export const Column = styled.div<{ $alignRight?: boolean }>`
  align-items: ${(o) => (o.$alignRight ? 'flex-end' : 'flex-start')};
  display: flex;
  flex: 1;
  flex-direction: column;
  text-align: ${(o) => (o.$alignRight ? 'right' : 'left')};
`

export const Label = styled.span`
  ${Typography.Overline2}
  color: rgba(255, 255, 255, 0.7);
  letter-spacing: 0.06em;
  text-transform: uppercase;
`

export const CountdownValue = styled.span<{ $warning?: boolean }>`
  ${Typography.Title2}
  color: ${(o) => (o.$warning ? Colors.Semantic.Warning.onclick : 'white')};
`

export const AmountValue = styled.span`
  ${Typography.Title2}
  color: white;
  font-family: 'Proxima Nova';
`

export const SubRow = styled.div`
  align-items: center;
  color: ${Colors.Semantic.BStock.background.lightBackground};
  display: flex;
  gap: 0.5rem;

  &:empty {
    display: none;
  }
`

const statItemStyles = css`
  ${Typography.Body4}
  align-items: center;
  display: flex;
  gap: 0.25rem;
`

export const StatItem = styled.span`
  ${statItemStyles}
`

export const StatButton = styled.button`
  ${statItemStyles}
  background: none;
  border: none;
  color: inherit;
  cursor: pointer;
  font-family: inherit;
  padding: 0;

  &:hover {
    text-decoration: underline;
  }
`

export const MsrpRow = styled.div`
  align-items: center;
  color: rgba(255, 255, 255, 0.65);
  display: flex;
`

export const MsrpText = styled.span`
  ${Typography.Body4}
`

export const InfoIcon = styled(InteractiveIcon)`
  color: inherit;
  font-size: 0.75rem;

  &:hover {
    color: white;
  }
`

/**
 * Actions chrome — shared by BidActions and SimpleOfferActions.
 */

export const ActionsBody = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
`

export const ActionCardBody = styled.div`
  border-top: ${pxToRem(1)} solid ${Colors.Semantic.Neutral.medium_grey};
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
  padding: 0.75rem 1rem;

  &:empty {
    display: none;
  }
`

export const SectionHeader = styled.div`
  align-items: center;
  color: ${Colors.Semantic.Neutral.black};
  display: flex;
  font-family: 'Proxima Nova';
  font-size: 1.125rem;
  font-style: normal;
  font-weight: 700;
  line-height: 1.625rem;
`

export const SectionInfoIcon = styled(InteractiveIcon)`
  color: ${Colors.Semantic.Neutral.dark_grey};
`

export const Bold = styled.span`
  font-weight: bold;
`

export const SectionContent = styled.div`
  ${Typography.Body3}
  color: ${Colors.Semantic.Neutral.dark_grey};
  white-space: pre-wrap;
`

export const ChargeDisclaimer = styled.div`
  ${Typography.Body4}
  color: ${Colors.Semantic.BStock.disabled};
  /* Exception to the container vertical rhythm: hug input below */
  margin-bottom: -0.25rem;
  margin-top: 0.25rem;
`

export const QuickActionButtonRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 0.5rem;
`

// Quick Bid, Quick Offer
export const QuickActionButton = styled.button`
  ${Typography.Body2}
  background-color: ${Colors.Semantic.Neutral.white};
  border: ${pxToRem(1)} solid ${Colors.Semantic.BStock.disabled};
  border-radius: 0.5rem;
  color: ${Colors.Semantic.Neutral.black};
  cursor: pointer;
  flex: 1;
  font-weight: 500;
  padding: 0.5rem 1rem;
  transition: all 0.2s ease;

  &:hover:not(:disabled) {
    background-color: ${Colors.Semantic.Neutral.light_grey};
    border-color: ${Colors.Semantic.Neutral.dark_grey};
  }

  &:active:not(:disabled) {
    background-color: ${Colors.Semantic.Neutral.light_grey};
    transform: scale(0.98);
  }

  &:disabled {
    background-color: ${Colors.Semantic.Neutral.light_grey};
    border-color: ${Colors.Semantic.Neutral.medium_grey};
    color: ${Colors.Semantic.Neutral.medium_grey};
    cursor: not-allowed;
  }
`

export const ActionCardInput = styled.div`
  [data-testid='input-container'] {
    border-radius: 0.5rem;
  }

  [data-testid='input-container']:not(:focus-within) {
    border-color: #d1d5db;
  }

  [data-testid='input-container'] > span {
    ${Typography.Subtitle2}
  }
`

export const ActionButton = styled(Button)<{ appearance?: string }>`
  ${Typography.Subtitle2}
  align-items: center;
  align-self: stretch;
  border-radius: 0.5rem;
  display: flex;
  flex-direction: row;
  gap: 0.5rem;
  height: 2.75rem;
  justify-content: center;
  padding: 0.6875rem 1.5rem;
  width: 100%;

  ${({ appearance }) =>
    (!appearance || appearance === 'primary') &&
    `
    && {
      background-color: ${Colors.Semantic.Warning.highlight};
      border-color: transparent;

      &:hover {
        background-color: ${Colors.Semantic.Warning.onhover};
      }

      &:active {
        background-color: ${Colors.Semantic.Warning.onclick};
      }

      &:disabled {
        background-color: ${Colors.Semantic.Neutral.medium_grey};
        cursor: default;
      }
    }
  `}
`
